import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { emailify, phoneify, rutify } from '../apiUtils'
import { IRequest, RequestStatus } from './RequestsServices'
import { RequestedChange } from './ChangesServices'
import { IUser } from './UsersServices'
import { ITripulante } from './TripulantesServices'
import { OperatedRepresentante } from './RepresentantesServices'

export interface IArmador {
  id: number
  tipo_persona: 'natural' | 'juridica'
  razon_social: string
  rut_empresa: string
  nombre_representante: string
  rut_representante: string
  telefono_representante: string
  email_representante: string
  imagen_perfil: string
  fecha_creacion: string
  fecha_actualizacion: string
  usuario_creacion: number
  activo: boolean
  id_personeria: number
  id_vigencia_pj?: number
  id_vigencia_personeria?: number
  autorizacion: number
  domicilio?: string
}

export interface OperatedArmador extends IArmador {
  requestData: IRequest | undefined
  changesData: RequestedChange[]
  armadorUser?: IUser
  armadorTripulante?: ITripulante
  status?: RequestStatus
  representantes: OperatedRepresentante[]
}

export const defaultIArmador: IArmador = {
  id: 0,
  tipo_persona: 'natural',
  razon_social: '',
  rut_empresa: '',
  nombre_representante: '',
  rut_representante: '',
  telefono_representante: '',
  email_representante: '',
  imagen_perfil: '',
  fecha_creacion: '',
  fecha_actualizacion: '',
  usuario_creacion: 0,
  activo: false,
  id_personeria: 0,
  id_vigencia_pj: 0,
  id_vigencia_personeria: 0,
  autorizacion: 0,
  domicilio: '',
}

/**
 * Datos de contacto de un armador para notificaciones (WhatsApp / correo).
 *
 * Para los armadores JURÍDICOS, getAllArmadores vacía intencionalmente telefono_representante y
 * email_representante al leer (el contacto de la empresa no es un dato propio, sino el del usuario
 * representante legal vigente, resuelto por join vía rut_representante). Por eso, leer
 * armador.telefono_representante / email_representante directamente devuelve '' para jurídicos y los
 * envíos caían en NaN. Estos helpers unifican la fuente: jurídico → registro del usuario legal;
 * natural → dato del propio armador.
 */
export function getArmadorContactPhone(
  armador: Pick<IArmador, 'tipo_persona' | 'telefono_representante' | 'rut_representante'>,
): string {
  if (armador.tipo_persona === 'juridica') {
    const user = dataStorageManager.searchDataInBucket<IUser>(
      BucketDefaultIDs.USERS,
      (u) => rutify(u.rut) === rutify(armador.rut_representante),
    )
    return user?.telefono ?? ''
  }
  return armador.telefono_representante
}

export function getArmadorContactEmail(
  armador: Pick<IArmador, 'tipo_persona' | 'email_representante' | 'rut_representante'>,
): string {
  if (armador.tipo_persona === 'juridica') {
    const user = dataStorageManager.searchDataInBucket<IUser>(
      BucketDefaultIDs.USERS,
      (u) => rutify(u.rut) === rutify(armador.rut_representante),
    )
    return user?.email ?? ''
  }
  return armador.email_representante
}

export default class ArmadoresServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para armadores
    this.bucketID = dataStorageManager.registerBucket<IArmador>(
      this.getAllArmadores.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.ARMADORES, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertArmador',
            {
              operation: this.insertArmador.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateArmador',
            {
              operation: this.updateArmador.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllArmadores(): Promise<IArmador[]> {
    try {
      const response = await dataQueryManager.addQuery<IArmador | IArmador[]>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_select_armadores`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data.map((armador) => {
          return {
            ...armador,
            rut_empresa: armador.rut_empresa ? rutify(armador.rut_empresa) : '',
            rut_representante: armador.rut_representante ? rutify(armador.rut_representante) : '',
            email_representante: armador.tipo_persona == 'natural' ? emailify(armador.email_representante) : '',
            telefono_representante: armador.tipo_persona == 'natural' ? phoneify(armador.telefono_representante) : '',
            id_personeria: typeof armador.id_personeria === 'number' ? armador.id_personeria : 0,
            id_vigencia_pj: typeof (armador as any).id_vigencia_pj === 'number' ? (armador as any).id_vigencia_pj : 0,
            id_vigencia_personeria:
              typeof (armador as any).id_vigencia_personeria === 'number'
                ? (armador as any).id_vigencia_personeria
                : 0,
          }
        })
      } else if (response.data) {
        return [response.data].map((armador) => {
          return {
            ...armador,
            rut_empresa: armador.rut_empresa ? rutify(armador.rut_empresa) : '',
            rut_representante: armador.rut_representante ? rutify(armador.rut_representante) : '',
            email_representante: armador.tipo_persona == 'natural' ? emailify(armador.email_representante) : '',
            telefono_representante: armador.tipo_persona == 'natural' ? phoneify(armador.telefono_representante) : '',
            id_personeria: typeof armador.id_personeria === 'number' ? armador.id_personeria : 0,
            id_vigencia_pj: typeof (armador as any).id_vigencia_pj === 'number' ? (armador as any).id_vigencia_pj : 0,
            id_vigencia_personeria:
              typeof (armador as any).id_vigencia_personeria === 'number'
                ? (armador as any).id_vigencia_personeria
                : 0,
          }
        })
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  async insertArmador(armadorData: IArmador): Promise<number | false> {
    try {
      const body = {
        op_insert_armador: {
          tipo_persona: armadorData.tipo_persona,
          razon_social: armadorData.razon_social,
          rut_empresa: armadorData.rut_empresa != '' ? rutify(armadorData.rut_empresa) : '',
          nombre_representante: armadorData.nombre_representante,
          rut_representante: rutify(armadorData.rut_representante),
          telefono_representante: armadorData.telefono_representante,
          email_representante: emailify(armadorData.email_representante),
          imagen_perfil: armadorData.imagen_perfil ? parseInt(armadorData.imagen_perfil.toString()) : 0,
          usuario_creacion: armadorData.usuario_creacion,
          id_personeria: parseInt(armadorData.id_personeria.toString()),
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_insert_armador`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response.data && typeof response.data === 'number') {
        // El backend devuelve el id del armador recién insertado: lo propagamos para que
        // el caller no dependa de re-buscarlo en la caché del bucket (que puede no estar
        // refrescada y dejaba registros huérfanos sin solicitud/representante).
        return response.data
      }
    } catch (error) {
      console.error('Error inserting armador:', error)
    }
    return false
  }

  async updateArmador(armadorData: IArmador): Promise<boolean> {
    try {
      // Telefono y email estan cambiados en api. Cambiar temporalmente hasta corregir en api
      const body = {
        op_update_armador: {
          tipo_persona: armadorData.tipo_persona,
          razon_social: armadorData.razon_social && armadorData.razon_social != '' ? armadorData.razon_social : '',
          rut_empresa: armadorData.rut_empresa && armadorData.rut_empresa != '' ? rutify(armadorData.rut_empresa) : '',
          nombre_representante: armadorData.nombre_representante,
          rut_representante: rutify(armadorData.rut_representante),
          email_representante: emailify(armadorData.email_representante),
          telefono_representante: phoneify(armadorData.telefono_representante),
          imagen_perfil: armadorData.imagen_perfil ? parseInt(armadorData.imagen_perfil.toString()) : 0,
          activo: armadorData.activo,
          id: armadorData.id,
          id_personeria: parseInt(armadorData.id_personeria.toString()),
          autorizacion: 0,
          estado: 1, // Revisar con backend. Api pide estado pero no esta en el modelo
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_update_armador`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating armador:', error)
    }
    return false
  }
}
